// Decoding + colour helpers for disaster scenario frames (base64 float16 / uint8 grids) — pure functions, no React.
const f16 = (h) => {
  const s = (h & 0x8000) ? -1 : 1, e = (h >> 10) & 0x1f, f = h & 0x3ff;
  if (e === 0) return s * Math.pow(2, -14) * (f / 1024);
  if (e === 31) return f ? NaN : s * Infinity;
  return s * Math.pow(2, e - 15) * (1 + f / 1024);
};
const bytes = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
export const decodeF16 = (b64) => { const u = bytes(b64); const v = new Uint16Array(u.buffer, u.byteOffset, u.byteLength / 2); const out = new Float32Array(v.length); for (let i = 0; i < v.length; i++) out[i] = f16(v[i]); return out; };
export const decodeU8 = (b64) => bytes(b64);

export const HAZARD_COLORS = { fire: "#FB923C", flood: "#38BDF8", landslide: "#A16207", cascade: "#C084FC" };
export const RAMPS = {
  slope: (v) => [255 * v, 160 * (1 - v) + 60, 40, 150 * v + 40],
  lowlying: (v) => [40, 120 * v + 40, 255, 190 * v],
  twi: (v) => [40, 200 * v, 200 * v + 55, 170 * v],
  fuel: (v) => { const c = Math.round(v * 255 / 80); return [[0, 0, 0, 0], [163, 230, 53, 150], [217, 119, 6, 160], [22, 101, 52, 170]][Math.min(3, c)]; },
  landslide: (v) => [255 * v, 60 * (1 - v), 30, 200 * v],
  aspect: (v) => { const a = v * 2 * Math.PI; return [128 + 120 * Math.cos(a), 128 + 120 * Math.sin(a), 160, 140]; },
  water: (v) => [30, 90, 200, 220 * v],
};

export const hillshade = (elev, nx, ny, cell, exag = 1) => {
  const az = (315 * Math.PI) / 180, alt = (45 * Math.PI) / 180, out = new Float32Array(nx * ny);
  for (let r = 0; r < ny; r++) for (let c = 0; c < nx; c++) {
    const g = (rr, cc) => elev[Math.min(ny - 1, Math.max(0, rr)) * nx + Math.min(nx - 1, Math.max(0, cc))];
    const dzdx = ((g(r, c + 1) - g(r, c - 1)) / (2 * cell)) * exag, dzdy = ((g(r + 1, c) - g(r - 1, c)) / (2 * cell)) * exag;
    const slope = Math.atan(Math.hypot(dzdx, dzdy)), aspect = Math.atan2(dzdy, -dzdx);
    out[r * nx + c] = Math.max(0, Math.sin(alt) * Math.cos(slope) + Math.cos(alt) * Math.sin(slope) * Math.cos(az - aspect));
  }
  return out;
};

export const hypso = (t) => { // 0..1 → RGB (green lowlands → ochre → grey → white)
  const stops = [[0, [34, 94, 56]], [0.25, [86, 140, 60]], [0.5, [176, 146, 84]], [0.75, [140, 130, 120]], [1, [235, 235, 240]]];
  for (let i = 1; i < stops.length; i++) if (t <= stops[i][0]) { const [a, ca] = stops[i - 1], [b, cb] = stops[i], k = (t - a) / (b - a); return ca.map((x, j) => x + (cb[j] - x) * k); }
  return stops[stops.length - 1][1];
};

// fire / landslide: state from arrival-time grid at time t (minutes)
export const fireRGBA = (arr, i, t, burn, out, o) => {
  const a = arr[i];
  if (a < 0 || a > t) return;
  if (t < a + burn) { const k = (t - a) / burn; out[o] = 255; out[o + 1] = 180 - 140 * k; out[o + 2] = 20; out[o + 3] = 235; } else { out[o] = 40; out[o + 1] = 30; out[o + 2] = 30; out[o + 3] = 200; }
};
export const slideRGBA = (arr, i, t, out, o) => {
  const a = arr[i];
  if (a < 0 || a > t) return;
  if (a === 0) { out[o] = 220; out[o + 1] = 38; out[o + 2] = 38; out[o + 3] = 230; } else { out[o] = 161; out[o + 1] = 98; out[o + 2] = 7; out[o + 3] = 210; }
};
// flood: interpolate quantised keyframes (uint8 = depth × 50)
export const floodDepth = (frames, t) => {
  if (!frames.length) return null;
  let j = frames.findIndex((f) => f.t_h >= t);
  if (j < 0) return frames[frames.length - 1].d;
  if (j === 0) return frames[0].d;
  const a = frames[j - 1], b = frames[j], k = (t - a.t_h) / Math.max(1e-6, b.t_h - a.t_h), out = new Float32Array(a.d.length);
  for (let i = 0; i < out.length; i++) out[i] = (a.d[i] + (b.d[i] - a.d[i]) * k) / 50;
  return out;
};
export const floodRGBA = (depth, out, o) => {
  if (depth < 0.05) return;
  const k = Math.min(1, depth / 3);
  out[o] = 30 * (1 - k); out[o + 1] = 160 - 120 * k; out[o + 2] = 255; out[o + 3] = 120 + 120 * k;
};
export const bresenham = (r0, c0, r1, c1) => { const pts = []; let dr = Math.abs(r1 - r0), dc = -Math.abs(c1 - c0), sr = r0 < r1 ? 1 : -1, sc = c0 < c1 ? 1 : -1, err = dr + dc; for (;;) { pts.push([r0, c0]); if (r0 === r1 && c0 === c1) break; const e2 = 2 * err; if (e2 >= dc) { err += dc; r0 += sr; } if (e2 <= dr) { err += dr; c0 += sc; } } return pts; };

// SWE / runout physics views: velocity keyframes (uint8 = m/s × 25), hazard rating HR = h·(v+0.5), deposit / flow height (m), failure time (h)
export const floodVel = (frames, t) => {
  if (!frames.length || !frames[0].v) return null;
  let j = frames.findIndex((f) => f.t_h >= t);
  if (j < 0) return Float32Array.from(frames[frames.length - 1].v, (x) => x / 25);
  if (j === 0) return Float32Array.from(frames[0].v, (x) => x / 25);
  const a = frames[j - 1], b = frames[j], k = (t - a.t_h) / Math.max(1e-6, b.t_h - a.t_h), out = new Float32Array(a.v.length);
  for (let i = 0; i < out.length; i++) out[i] = (a.v[i] + (b.v[i] - a.v[i]) * k) / 25;
  return out;
};
export const velRGBA = (v, out, o) => { if (v < 0.05) return; const k = Math.min(1, v / 5); out[o] = 255 * k; out[o + 1] = 230 - 200 * k; out[o + 2] = 60 * (1 - k); out[o + 3] = 120 + 120 * k; };
export const hrRGBA = (hr, out, o) => { if (hr < 0.1) return; const c = hr >= 2.5 ? [239, 68, 68, 235] : hr >= 1.25 ? [249, 115, 22, 220] : hr >= 0.75 ? [250, 204, 21, 200] : [163, 230, 53, 170]; out[o] = c[0]; out[o + 1] = c[1]; out[o + 2] = c[2]; out[o + 3] = c[3]; };
export const depositRGBA = (h, out, o) => { if (h < 0.05) return; const k = Math.min(1, h / 4); out[o] = 180 - 60 * k; out[o + 1] = 100 - 70 * k; out[o + 2] = 20; out[o + 3] = 150 + 100 * k; };
export const HR_LEGEND = [["< 0.75", "low", "#A3E635"], ["0.75–1.25", "moderate", "#FACC15"], ["1.25–2.5", "significant", "#F97316"], ["≥ 2.5", "extreme", "#EF4444"]];

// Observation overlays: fused moisture θ₀ (dry ochre → wet blue), SAR footprint (magenta), SAR-vs-simulation superposition, cascade reservoirs
export const moistRGBA = (v, out, o) => { if (!(v >= 0) || out[o + 3] > 0) return; out[o] = 200 - 170 * v; out[o + 1] = 120 + 20 * v; out[o + 2] = 60 + 195 * v; out[o + 3] = 175; };
export const sarRGBA = (m, out, o) => { if (!m) return; out[o] = 236; out[o + 1] = 72; out[o + 2] = 153; out[o + 3] = 205; };
const SUPER = { 1: [0, 255, 127, 225], 2: [56, 189, 248, 225], 3: [239, 68, 68, 225] };
export const superRGBA = (k, out, o) => { const c = SUPER[k]; if (!c) return; out[o] = c[0]; out[o + 1] = c[1]; out[o + 2] = c[2]; out[o + 3] = c[3]; };
export const reservoirRGBA = (m, out, o) => { if (!m) return; out[o] = 14; out[o + 1] = 116; out[o + 2] = 144; out[o + 3] = 215; };
export const SUPER_LEGEND = [["hit", "#00FF7F"], ["miss · observed only", "#38BDF8"], ["false alarm · simulated only", "#EF4444"]];
export const decodeMoist = (b64) => Float32Array.from(decodeU8(b64), (x) => (x === 255 ? NaN : x / 254));
